import React from 'react';
import { useChatStore } from '../../store/chatStore';
import {
  formatFileSize,
  getFileTypeCategory,
  FileUploadProcessingResult,
} from '../../../../utils/fileUtils';

import { useTranslation } from 'react-i18next';

export interface AttachedFile {
  id: string;
  file: File;
  preview?: string; // For images, we can show a preview
}

export interface UploadedFilePreview {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  isUploaded: boolean;
}

interface FilePreviewProps {
  attachedFiles?: AttachedFile[];
  uploadedFiles?: FileUploadProcessingResult[];
  onRemoveFile?: (fileId: string) => void;
  onRemoveUploadedFile?: (index: number) => void;
}

const FilePreviewItem: React.FC<{
  file: AttachedFile | UploadedFilePreview;
  onRemove: () => void;
  isUploaded?: boolean;
}> = ({ file, onRemove, isUploaded: _isUploaded }) => {
  const { t } = useTranslation('pleri/chat');
  const fileName = 'file' in file ? file.file.name : file.name;
  const fileSize = 'file' in file ? file.file.size : file.size;
  const fileType =
    'file' in file
      ? getFileTypeCategory(file.file.type)
      : getFileTypeCategory(file.type);
  const preview =
    'preview' in file
      ? file.preview
      : 'url' in file && fileType === 'image'
        ? file.url
        : undefined;

  const getExtensionLabel = (): string => {
    const name = fileName || '';
    const extFromName = name.includes('.') ? name.split('.').pop() || '' : '';
    const mime = 'file' in file ? file.file.type : file.type;
    let ext = (extFromName || '').toUpperCase();
    if (!ext) {
      if (mime.includes('pdf')) {
        ext = 'PDF';
      } else if (mime.includes('json')) {
        ext = 'JSON';
      } else if (mime.includes('csv')) {
        ext = 'CSV';
      } else if (mime.includes('yaml') || mime.includes('yml')) {
        ext = 'YAML';
      } else if (mime.includes('xml')) {
        ext = 'XML';
      } else if (mime.includes('plain')) {
        ext = 'TXT';
      } else if (mime.includes('sheet')) {
        ext = 'XLSX';
      } else if (mime.includes('excel')) {
        ext = 'XLS';
      } else if (mime.startsWith('image/')) {
        ext = mime.replace('image/', '').toUpperCase();
      }
    }
    return ext.slice(0, 5);
  };

  // Removed unused accent pill function

  const extLabel = getExtensionLabel();

  const getIconBgClass = (ext: string): string => {
    switch (ext) {
      case 'PDF':
        return 'bg-blue-50 dark:bg-blue-950';
      case 'CSV':
        return 'bg-emerald-50 dark:bg-emerald-950';
      case 'JSON':
        return 'bg-amber-50 dark:bg-amber-950';
      case 'YAML':
        return 'bg-violet-50 dark:bg-violet-950';
      case 'XML':
        return 'bg-indigo-50 dark:bg-indigo-950';
      case 'TXT':
        return 'bg-slate-500';
      case 'XLS':
      case 'XLSX':
        return 'bg-green-50 dark:bg-green-950';
      default:
        return 'bg-blue-50 dark:bg-blue-950';
    }
  };

  const getFileIcon = () => {
    switch (fileType) {
      case 'image':
        return (
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        );
      case 'document':
        return (
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        );
    }
  };

  // Render differently for images vs other files
  if (fileType === 'image') {
    return (
      <div
        className="group relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
        title={fileName}
      >
        {preview ? (
          <img
            src={preview}
            alt={fileName}
            className="h-full w-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-600 dark:text-gray-400">
            {getFileIcon()}
          </div>
        )}
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-0.5 right-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-white/80 text-gray-700 ring-1 ring-gray-200 transition hover:bg-white dark:bg-gray-900/80 dark:text-gray-300 dark:ring-gray-700"
          aria-label={t('removeFile', { fileName })}
          title={t('common:remove')}
        >
          <svg
            className="h-3 w-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <span className="sr-only">
          {fileName} • {formatFileSize(fileSize)}
        </span>
      </div>
    );
  }

  // Non-images: larger card with filename and size
  return (
    <div
      className="group relative inline-flex h-16 max-w-[24rem] min-w-[14rem] flex-shrink-0 items-center gap-3 truncate rounded-2xl border border-gray-200 bg-white px-4 pr-8 shadow-sm dark:border-gray-700 dark:bg-gray-900"
      title={fileName}
    >
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl ${getIconBgClass(extLabel)} text-white`}
      >
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
          <path d="M14 2v6h6" className="opacity-90" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
          {fileName}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {extLabel || 'FILE'} • {formatFileSize(fileSize)}
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-black/70 text-white transition hover:bg-black"
        aria-label={t('removeFile', { fileName })}
        title={t('common:remove')}
      >
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      <span className="sr-only">
        {fileName} • {formatFileSize(fileSize)}
      </span>
    </div>
  );
};

export const FilePreview: React.FC<FilePreviewProps> = ({
  attachedFiles = [],
  uploadedFiles = [],
  onRemoveFile,
  onRemoveUploadedFile,
}) => {
  const { t } = useTranslation('pleri/chat');
  const setAttachedFiles = useChatStore((s) => s.setAttachedFiles);
  const setUploadedFiles = useChatStore((s) => s.setUploadedFiles);
  const totalFiles = attachedFiles.length + uploadedFiles.length;

  if (totalFiles === 0) {
    return null;
  }

  // Convert uploaded files to preview format
  const uploadedFilePreviews: UploadedFilePreview[] = uploadedFiles.map(
    (result, index) => ({
      id: `uploaded-${index}`,
      name: result.uploadResult?.fileName || 'Unknown file',
      size: result.uploadResult?.fileSize || 0,
      type: result.uploadResult?.fileType || '',
      url: result.uploadResult?.fileUrl || '',
      isUploaded: true,
    }),
  );

  return (
    <div className="mb-1">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className="h-4 w-4 text-gray-600 dark:text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {t('attachedFiles', { totalFiles })}
          </span>
        </div>
        {totalFiles > 0 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 shadow-sm hover:bg-gray-50 active:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
            onClick={() => {
              attachedFiles.forEach((f) => {
                if (f.preview) {
                  URL.revokeObjectURL(f.preview);
                }
              });
              setAttachedFiles([]);
              setUploadedFiles([]);
            }}
            title={t('clearAllAttachments')}
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            {t('clearAll')}
          </button>
        )}
      </div>
      <div className="custom-scrollbar overflow-x-auto pr-1">
        <div className="flex items-center gap-2 py-1">
          {attachedFiles.map((attachedFile) => (
            <FilePreviewItem
              key={attachedFile.id}
              file={attachedFile}
              onRemove={() => onRemoveFile?.(attachedFile.id)}
              isUploaded={false}
            />
          ))}
          {uploadedFilePreviews.map((uploadedFile, index) => (
            <FilePreviewItem
              key={uploadedFile.id}
              file={uploadedFile}
              onRemove={() => onRemoveUploadedFile?.(index)}
              isUploaded={true}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FilePreview;
